import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { ArrowBack } from '@mui/icons-material';
import ImageComponent from '../components/ImageComponent'
import ScanResultTable from '../components/ScanResultTable'
import { getSubScans, getScanDetails, updateScanResult, getImageFromCanvas } from '../utils/api';
import CircularProgress from '@mui/material/CircularProgress';
import { successMessage, errorMessage } from '../components/Toaster';
import { getErrorMessage } from "../utils/error";
import { GetScanResultMerged } from '../utils/utils';

const ScanDetails = () => {
  const navigate = useNavigate();
  const path = window.location.pathname;
  const segments = path.split("/");
  const userId = segments[2];
  const [scanDetails, setScanDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cropImageUrls, setCropImageUrls] = useState(null);
  const [cropImageUrlsForBottom, setCropImageUrlsForBottom] = useState(null);
  const [isCanvasLoading, setIsCanvasLoading] = useState(false);
  const [kernelDetails, setKernalDetails] = useState(null);
  const [scanID, SetScanID] = useState(null);
  const [subScanID, setSubScanID] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [classNameCard, setClassNameCard] = useState('');
  const [ledCropImageUrls, setLedCropImageUrls] = useState([]);
  const [ledCropImageUrlsForBottom, setLedCropImageUrlsForBottom] = useState([]);
  const [subScanList, setSubScanList] = useState([]);
  const [activeSubScanId, setActiveSubScanId] = useState(null);
  const [subScansCache, setSubScansCache] = useState({});
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [cropBboxes, setCropBboxes] = useState([]);
  const [cropBboxesBottom, setCropBboxesBottom] = useState([]);
  const [imagesReady, setImagesReady] = useState(false);
  const preloadedImagesRef = useRef({});

  const addIdToBboxes = (bboxesObj) => {
    if (!bboxesObj) return bboxesObj;
    const updated = {};
    Object.keys(bboxesObj).forEach((className) => {
      updated[className] = (bboxesObj[className] || []).map((box, index) => ({
        ...box,
        id: box?.id ?? `${className}-${index}-${Math.random().toString(36).slice(2)}`,
      }));
    });
    return updated;
  };

  // ✅ Guard undefined/null/empty url
  const downloadImageToBlob = async (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  };

  // ✅ Only download what exists — rest will be null safely
  const preloadImagesForSubScan = async (subScanId, files) => {
    if (!files || files.length === 0) {
      preloadedImagesRef.current[subScanId] = { top: null, bottom: null, ledTop: null, ledBottom: null };
      return;
    }

    const topFile =
      files.find((f) =>
        ['captured.webp', 'captured.png', 'captured.jpeg', 'captured.jpg'].includes(f.file_name)
      ) || files.find((f) => f.file_name?.includes('_cv_server'));

    const bottomFile =
      files.find((f) => f.file_name?.includes('captured_bottom')) ||
      files.find((f) => f.file_name?.includes('_bottom_cv_server'));

    const ledTopFile = files.find((f) => f.file_name?.includes('captured_led'));
    const ledBottomFile = files.find((f) => f.file_name?.includes('captured_bottom_led'));

    const [top, bottom, ledTop, ledBottom] = await Promise.all([
      downloadImageToBlob(topFile?.file_url),
      downloadImageToBlob(bottomFile?.file_url),
      downloadImageToBlob(ledTopFile?.file_url),
      downloadImageToBlob(ledBottomFile?.file_url),
    ]);

    preloadedImagesRef.current[subScanId] = { top, bottom, ledTop, ledBottom };
  };

  useEffect(() => {
    const fetchScans = async () => {
      try {
        setIsLoading(true); // ✅ single loader starts

        const path = window.location.pathname;
        const regex = /\/users\/[^/]+\/([^/]+)/;
        const match = path.match(regex);

        if (match && match.length >= 2) {
          const currentScanID = match[1];
          SetScanID(currentScanID);

          const subScansRes = await getSubScans(currentScanID);
          const subScanIds = subScansRes?.data?.data || [];
          setSubScanList(subScanIds);

          if (subScanIds.length === 0) {
            setIsLoading(false);
            return;
          }

          const firstSubScanId = subScanIds[0];
          const firstScanData = await getScanDetails({
            scan_id: currentScanID,
            sub_scan_id: firstSubScanId,
          });

          const firstDetails = firstScanData?.data?.data[0];
          const firstProcessed = {
            ...firstDetails?.processed_image,
            bboxes: addIdToBboxes(firstDetails?.processed_image?.bboxes),
            bboxes_bottom: addIdToBboxes(
              firstDetails?.cropped_kernel_details?.bboxes_bottom
            ),
          };

          const initialCache = {
            [firstSubScanId]: {
              scanDetails: firstDetails,
              processData: firstProcessed,
              kernelDetails: firstDetails?.kernel_details,
            },
          };

          setSubScansCache(initialCache);
          setActiveSubScanId(firstSubScanId);
          setSubScanID(firstSubScanId);
          setScanDetails(firstDetails);
          setKernalDetails(firstDetails?.kernel_details);
          setProcessData(firstProcessed);

          // ✅ Preload images BEFORE removing loader — all under one isLoading
          const firstFiles = firstDetails?.scan_files || [];
          await preloadImagesForSubScan(firstSubScanId, firstFiles);
          setImagesReady(true);

          // ✅ Single loader ends — data + images both ready
          setIsLoading(false);

          // ✅ Fetch + preload remaining sub scans in background silently
          if (subScanIds.length > 1) {
            setTimeout(async () => {
              const remaining = subScanIds.slice(1);
              for (const subId of remaining) {
                try {
                  const scanData = await getScanDetails({
                    scan_id: currentScanID,
                    sub_scan_id: subId,
                  });
                  if (scanData?.data) {
                    const details = scanData?.data?.data[0];
                    const processedData = {
                      ...details?.processed_image,
                      bboxes: addIdToBboxes(details?.processed_image?.bboxes),
                      bboxes_bottom: addIdToBboxes(
                        details?.cropped_kernel_details?.bboxes_bottom
                      ),
                    };
                    setSubScansCache((prev) => ({
                      ...prev,
                      [subId]: {
                        scanDetails: details,
                        processData: processedData,
                        kernelDetails: details?.kernel_details,
                      },
                    }));
                    await preloadImagesForSubScan(subId, details?.scan_files || []);
                  }
                } catch (err) {
                  console.error(`Failed to fetch sub scan ${subId}:`, err);
                }
              }
            }, 100);
          }
        }
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    };

    fetchScans();

    return () => {
      Object.values(preloadedImagesRef.current).forEach(({ top, bottom, ledTop, ledBottom } = {}) => {
        if (top) URL.revokeObjectURL(top);
        if (bottom) URL.revokeObjectURL(bottom);
        if (ledTop) URL.revokeObjectURL(ledTop);
        if (ledBottom) URL.revokeObjectURL(ledBottom);
      });
    };
  }, [window.location.pathname]);

  const handleTabSwitch = async (targetSubScanId) => {
    if (activeSubScanId === targetSubScanId) return;

    setSubScansCache((prev) => ({
      ...prev,
      [activeSubScanId]: {
        ...prev[activeSubScanId],
        scanDetails,
        processData,
        kernelDetails,
      },
    }));

    const cached = subScansCache[targetSubScanId];
    if (cached) {
      setScanDetails(cached.scanDetails);
      setKernalDetails(cached.kernelDetails);
      setProcessData(cached.processData);
      setSubScanID(targetSubScanId);
    }

    setCropImageUrls(null);
    setCropImageUrlsForBottom(null);
    setClassNameCard('');
    setActiveSubScanId(targetSubScanId);

    // ✅ Tab switch — images not ready yet, show small loader on buttons only
    if (!preloadedImagesRef.current[targetSubScanId]) {
      setImagesReady(false);
      const files = cached?.scanDetails?.scan_files || [];
      await preloadImagesForSubScan(targetSubScanId, files);
      setImagesReady(true);
    }
    // ✅ Already preloaded in background — instant, no loader needed
  };

  const handleCropImage = async (className) => {
    if (!imagesReady) return;

    setClassNameCard(className);
    setIsCanvasLoading(true);
    setCropImageUrls(null);
    setCropImageUrlsForBottom(null);
    setLedCropImageUrls([]);
    setLedCropImageUrlsForBottom([]);
    setCropBboxes(getBoundingBoxByClassName(className) || []);
    setCropBboxesBottom(getBoundingBoxForBottomByClassName(className) || []);

    await new Promise((resolve) => setTimeout(resolve, 0));

    const preloaded = preloadedImagesRef.current[activeSubScanId] || {};
    const capturedWebpUrl = preloaded.top || null;
    const capturedWebpUrlForBottom = preloaded.bottom || null;
    const capturedLedUrl = preloaded.ledTop || null;
    const capturedLedUrlForBottom = preloaded.ledBottom || null;

    const boundingBoxStrings =
      getBoundingBoxByClassName(className)?.map(JSON.stringify) || [];
    const boundingBoxStringsForBottom =
      getBoundingBoxForBottomByClassName(className)?.map(JSON.stringify) || [];

    const allCropped = [];
    const allCroppedBottom = [];
    const allLed = [];
    const allLedBottom = [];

    const chunkSize = 3;
    const totalChunks = Math.max(
      Math.ceil(boundingBoxStrings.length / chunkSize),
      Math.ceil(boundingBoxStringsForBottom.length / chunkSize),
      1
    );

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const chunk = boundingBoxStrings.slice(start, start + chunkSize);
      const chunkBottom = boundingBoxStringsForBottom.slice(start, start + chunkSize);

      await new Promise((resolve) => setTimeout(resolve, 0));

      const [cropped, croppedBottom, led, ledBottom] = await Promise.all([
        Promise.all(
          chunk.map((box) =>
            capturedWebpUrl ? getImageFromCanvas(capturedWebpUrl, box, true) : null
          )
        ),
        Promise.all(
          chunkBottom.map((box) =>
            capturedWebpUrlForBottom
              ? getImageFromCanvas(capturedWebpUrlForBottom, box)
              : null
          )
        ),
        Promise.all(
          chunk.map((box) =>
            capturedLedUrl ? getImageFromCanvas(capturedLedUrl, box, true) : null
          )
        ),
        Promise.all(
          chunkBottom.map((box) =>
            capturedLedUrlForBottom
              ? getImageFromCanvas(capturedLedUrlForBottom, box)
              : null
          )
        ),
      ]);

      allCropped.push(...cropped);
      allCroppedBottom.push(...croppedBottom);
      allLed.push(...led);
      allLedBottom.push(...ledBottom);

      setCropImageUrls([...allCropped].filter(Boolean));
      setCropImageUrlsForBottom([...allCroppedBottom].filter(Boolean));
      setLedCropImageUrls([...allLed].filter(Boolean));
      setLedCropImageUrlsForBottom([...allLedBottom].filter(Boolean));

    }

    setIsCanvasLoading(false);
  };

  const getBoundingBoxByClassName = (className) => {
    if (processData?.bboxes && processData.bboxes.hasOwnProperty(className))
      return processData.bboxes[className];
    return null;
  };

  const getBoundingBoxForBottomByClassName = (className) => {
    if (processData?.bboxes_bottom && processData.bboxes_bottom.hasOwnProperty(className))
      return processData.bboxes_bottom[className];
    return null;
  };

  const cleanBBoxes = (bboxesObj) => {
    if (!bboxesObj) return bboxesObj;
    const cleaned = {};
    Object.keys(bboxesObj).forEach((className) => {
      cleaned[className] = (bboxesObj[className] || [])
        .filter((item) => item && Object.keys(item).length > 0)
        .map((item) => {
          if (item.coords) return item.coords;
          if (Array.isArray(item)) return item;
          return [item[0], item[1], item[2], item[3]];
        });
    });
    return cleaned;
  };

  const handleUpdateAllSubScans = async () => {
    const latestCache = {
      ...subScansCache,
      [activeSubScanId]: { scanDetails, processData, kernelDetails },
    };

    const subScansPayload = Object.entries(latestCache).map(([subScanId, data]) => ({
      scan_id: scanID,
      sub_scan_id: parseInt(subScanId),
      processed_image: {
        ...data.processData,
        bboxes: cleanBBoxes(data.processData?.bboxes),
        bboxes_bottom: cleanBBoxes(data.processData?.bboxes_bottom),
      },
      kernel_details: data.kernelDetails,
      kernel_distributions: data.scanDetails?.kernel_distributions,
      dist_avg_indexes: data.scanDetails?.dist_avg_indexes,
      alerts: data.scanDetails?.alerts,
      cropped_kernel_details: {
        ...data.scanDetails?.cropped_kernel_details,
        bboxes: cleanBBoxes(data.processData?.bboxes),
        bboxes_bottom: cleanBBoxes(data.processData?.bboxes_bottom),
      },
      versions: data.scanDetails?.versions,
      cv_variety_name: data.scanDetails?.cv_variety_name,
      success: true,
    }));

    try {
      setLoading(true);
      const results = await Promise.allSettled(
        subScansPayload.map((payload) => updateScanResult(payload))
      );

      const failed = results.filter((r) => {
        if (r.status === 'rejected') return true;
        if (r.value?.data?.status === false) return true;
        return false;
      });

      const succeeded = results.length - failed.length;

      if (failed.length === 0) {
        successMessage('scan updated successfully.');
        const allScanDetails = Object.values(latestCache).map((data) => ({
          ...data.scanDetails,
          kernel_details: data.kernelDetails,
          processed_image: data.processData,
        }));

        const firstEntry = Object.values(latestCache)[0];
        const weight = firstEntry?.scanDetails?.weight_value || 0;
        const moisture = firstEntry?.scanDetails?.moisture_value || 0;

        const mergedResult = GetScanResultMerged(allScanDetails, weight, moisture);
        setScanData(mergedResult);
        setIsTableOpen(true);
      } else if (succeeded === 0) {
        errorMessage('Failed to update scan.');
      } else {
        errorMessage(`${succeeded} updated, ${failed.length} failed.`);
      }
    } catch (error) {
      errorMessage(getErrorMessage(error) || 'Update failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoading || loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </div>
      ) : (
        <div className="scan-content-container">
          <div className="scan-container">

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 16 }}>
                {/* Back Arrow */}
                <ArrowBack
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/users/${userId}`)}
                />
                {/* Scan ID */}
                Scan ID: {scanID}
              </div>
              <Button variant="contained" color="primary" onClick={handleUpdateAllSubScans}>
                Update Scan
              </Button>
            </div>

            {subScanList.length >= 1 && (
              <div style={{ display: 'flex', gap: 8, margin: '16px 0', flexWrap: 'wrap' }}>
                {subScanList.map((subScanId) => (
                  <button
                    key={subScanId}
                    onClick={() => handleTabSwitch(subScanId)}
                    style={{
                      padding: '6px 18px',
                      borderRadius: 8,
                      border: activeSubScanId === subScanId ? '2px solid #1976d2' : '1px solid #ccc',
                      background: activeSubScanId === subScanId ? '#e3f2fd' : 'transparent',
                      color: activeSubScanId === subScanId ? '#1976d2' : '#555',
                      fontWeight: activeSubScanId === subScanId ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Sub Scan {subScanId}
                    {subScansCache[subScanId] && subScanId !== activeSubScanId && (
                      <span style={{
                        marginLeft: 6,
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: '#1976d2',
                        display: 'inline-block',
                      }} />
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="tab-content">
              {!imagesReady ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CircularProgress size={18} />
                  <span style={{ fontSize: 13, color: '#888' }}>Preparing images...</span>
                </div>
              ) : (
                kernelDetails?.classes && kernelDetails.classes.map((className, index) => (
                  <React.Fragment key={index}>
                    <Button
                      style={{
                        color: 'black',
                        border: `3px solid ${processData?.encoding_colors[className]}`,
                        ...(classNameCard === className && {
                          background: `${processData?.encoding_colors[className]}`,
                          color: 'white'
                        })
                      }}
                      onClick={() => { handleCropImage(className); }}
                    >
                      {`${className.replace(/_/g, ' ')} - ${processData?.encodings[className]} - ${kernelDetails?.kernel_counts[className] !== null ? kernelDetails?.kernel_counts[className] : 0}`}
                    </Button>
                  </React.Fragment>
                ))
              )}
            </div>

            <div>
              {isCanvasLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </div>
              ) : (
                <div>
                  {cropImageUrls && cropImageUrls.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <div className='tab-content'>
                        {cropImageUrls.map((imageUrl, index) => (
                          <React.Fragment key={`${imageUrl}-${index}`}>
                            <ImageComponent
                              key={index}
                              imageUrl={imageUrl}
                              imageUrlForBottom={cropImageUrlsForBottom ? cropImageUrlsForBottom[index] : ''}
                              ledImageUrl={ledCropImageUrls?.[index] || null}
                              ledImageUrlForBottom={ledCropImageUrlsForBottom?.[index] || null}
                              kernelDetails={kernelDetails}
                              classNameCard={classNameCard}
                              encodings={processData?.encodings}
                              scanId={scanID}
                              subScanId={subScanID}
                              scanDetails={scanDetails}
                              setKernalDetails={setKernalDetails}
                              setProcessData={setProcessData}
                              bbox={cropBboxes?.[index]}
                              bboxBottom={cropBboxesBottom?.[index]}
                            />
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ) : cropImageUrls && cropImageUrls.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <p>There is no image to show.</p>
                    </div>
                  ) : (
                    kernelDetails?.classes && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <p>Please Select Class</p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {isTableOpen && (
              <ScanResultTable
                open={isTableOpen}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                  setIsTableOpen(false);
                  navigate(`/users/${userId}`)
                }}
                scanId={scanID}
                userId={userId}
                scanResult={scanData}
                title='View Table'
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScanDetails;